import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query'

import useSessionStore from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'
import { getV6ApiUrl } from '@/utils/urlHelpers'

export interface SmsTrackingPayload {
  brand_id: string
  phone_number: string
  terms_accepted: boolean
  optin: boolean
}

export interface SmsTrackingResponse {
  data: {
    sms_tracking: {
      id: string
      phone_number: string
      terms_accepted: boolean
      optin: boolean
      optin_confirmed: boolean
    }
  }
  status: string
  code: number
}

const createSmsTracking = async (payload: SmsTrackingPayload): Promise<SmsTrackingResponse> => {
  const authToken = useSessionStore.getState().authToken

  if (!authToken) {
    throw new Error('Authentication required')
  }

  const smsTrackingUrl = getV6ApiUrl('/sms_tracking')
  const requestBody = { sms_tracking: payload }

  const response = await fetch(smsTrackingUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Brand-Id': brand.key,
      'X-User-Token': authToken,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage = errorData.data?.errors?.[0] || 'Failed to create SMS tracking'
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data
}

const updateSmsTracking = async (payload: SmsTrackingPayload): Promise<SmsTrackingResponse> => {
  const authToken = useSessionStore.getState().authToken

  if (!authToken) {
    throw new Error('Authentication required')
  }

  // Get the current user ID from the session store
  const userId = useSessionStore.getState().userId

  if (!userId) {
    throw new Error('User ID not found')
  }

  const smsTrackingUrl = getV6ApiUrl(`/sms_tracking/${userId}`)
  const requestBody = { sms_tracking: payload }

  const response = await fetch(smsTrackingUrl, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-User-Token': authToken,
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage = errorData.data?.errors?.[0] || 'Failed to update SMS tracking'
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data
}

const getSmsTracking = async (): Promise<SmsTrackingResponse> => {
  const authToken = useSessionStore.getState().authToken

  if (!authToken) {
    throw new Error('Authentication required')
  }

  const smsTrackingUrl = getV6ApiUrl('/sms_tracking')

  const response = await fetch(smsTrackingUrl, {
    method: 'GET',
    headers: {
      'X-User-Token': authToken,
    },
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage = errorData.data?.errors?.[0] || 'Failed to get SMS tracking'
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data
}

export const useCreateSmsTracking = (): UseMutationResult<
  SmsTrackingResponse,
  Error,
  SmsTrackingPayload
> => {
  return useMutation({
    mutationFn: createSmsTracking,
  })
}

export const useUpdateSmsTracking = (): UseMutationResult<
  SmsTrackingResponse,
  Error,
  SmsTrackingPayload
> => {
  return useMutation({
    mutationFn: updateSmsTracking,
  })
}

export const useGetSmsTracking = (
  enabled: boolean = true,
): UseQueryResult<SmsTrackingResponse, Error> => {
  return useQuery({
    queryKey: ['sms-tracking'],
    queryFn: getSmsTracking,
    enabled,
  })
}
